import React, { useState, useEffect } from 'react';
import { Button, Col, Input, Row, Select, Typography, DatePicker, message, Modal, Checkbox, Upload } from "antd";
import styled from "styled-components";
import dayjs from 'dayjs';
import { DeleteOutlined, UploadOutlined, DownloadOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { useLaboratoryId } from '../hooks/useLaboratoryId';
import type { MeasuringInstrumentType } from '../pages/tables/MeasuringInstruments';
import type { TestInstrumentType } from '../pages/tables/TestInstruments';
import type { HelpInstrumentType } from '../pages/tables/HelpInstruments';
import instrumentsApi from '../api/instruments';
import { INSTRUMENT_TYPES, InstrumentType } from '../constants/instrumentTypes';
import { getCustomColumns, CustomColumn } from '../api/customColumns';

const { Text } = Typography;

// Используем те же стили, что и в GetReagent
const ScreenContainer = styled.div`
  width: 400px;
  background: white;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
`;

const ScrollContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 16px;
  max-height: 500px;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: #f0f0f0;
  }

  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 2px;
  }
`;

const BottomButtons = styled.div`
  padding: 16px;
`;

const StyledInput = styled(Input)`
  height: 40px;
  background-color: #fafafa;
  border-radius: 4px;

  &[type='number'] {
    -moz-appearance: textfield;
    
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }
`;

const StyledSelect = styled(Select)`
  .ant-select-selector {
    height: 40px !important;
    padding: 4px 11px !important;
    background-color: #fafafa !important;
    border-radius: 4px !important;
  }
`;

const StyledDatePicker = styled(DatePicker)`
  height: 40px;
  background-color: #fafafa;
  border-radius: 4px;
`;

const StyledText = styled(Text)`
  color: #8c8c8c;
  font-weight: 500;
  font-size: 12px;
`;

const ButtonsRow = styled(Row)`
  display: flex;
  justify-content: center;
  gap: 8px;
  
  .ant-btn {
    height: 40px;
  }

  .cancel-button {
    flex: 1;
    max-width: 178px;
  }

  .action-button {
    flex: 1;
    max-width: 178px;
  }
`;

interface EditInstrumentProps {
  onCancel: () => void;
  record: MeasuringInstrumentType | TestInstrumentType | HelpInstrumentType;
  type: InstrumentType;
  onSuccess?: () => void;
}

const ScreenTitle = styled(Typography.Title)`
  margin: 16px !important;
  font-size: 16px !important;
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const EditInstrument: React.FC<EditInstrumentProps> = ({ onCancel, record, type, onSuccess }) => {
  const laboratoryId = useLaboratoryId();
  const { token } = theme.useToken();
  const [loading, setLoading] = useState(false);
  const [editedRecord, setEditedRecord] = useState(record);
  const [customColumns, setCustomColumns] = useState<CustomColumn[]>([]);
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, any>>({});
  const [ownershipDocumentFile, setOwnershipDocumentFile] = useState<File | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    setEditedRecord(record);
    // Инициализируем кастомные поля из выбранной записи
    if (record?.custom_columns) {
      setCustomFieldValues(record.custom_columns);
    } else {
      setCustomFieldValues({});
    }
  }, [record]);

  // Загружаем кастомные колонки при монтировании компонента
  useEffect(() => {
    const fetchCustomColumns = async () => {
      if (!laboratoryId) return;
      
      let registryType = '';
      switch (type) {
        case INSTRUMENT_TYPES.MEASURING:
          registryType = 'measuring_instrument';
          break;
        case INSTRUMENT_TYPES.TESTING:
          registryType = 'testing_instrument';
          break;
        case INSTRUMENT_TYPES.HELPING:
          registryType = 'helping_instrument';
          break;
      }
      
      try {
        const columns = await getCustomColumns(registryType, Number(laboratoryId));
        setCustomColumns(columns);
      } catch (error) {
        console.error('Ошибка при загрузке кастомных колонок:', error);
      }
    };

    fetchCustomColumns();
  }, [laboratoryId, type]);

  const handleSelectChange = (field: string) => (value: unknown) => {
    setEditedRecord(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDateChange = (field: string) => 
    (_: unknown, dateString: string | string[]) => {
      setEditedRecord(prev => ({
        ...prev,
        [field]: typeof dateString === 'string' ? dateString || null : null
      }));
    };

  const handleCustomFieldChange = (key: string, value: any) => {
    setCustomFieldValues(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Функция для рендеринга кастомного поля в зависимости от его типа
  const renderCustomField = (column: CustomColumn) => {
    const value = customFieldValues[column.key];
    
    switch (column.type) {
      case 'text':
        return (
          <StyledInput
            value={value || ''}
            onChange={(e) => handleCustomFieldChange(column.key, e.target.value)}
            placeholder={`Введите ${column.title.toLowerCase()}`}
          />
        );
      case 'int':
        return (
          <StyledInput
            type="number"
            value={value || ''}
            onChange={(e) => handleCustomFieldChange(column.key, e.target.value ? Number(e.target.value) : null)}
            placeholder={`Введите ${column.title.toLowerCase()}`}
          />
        );
      case 'date':
        return (
          <StyledDatePicker
            value={value ? dayjs(value) : null}
            onChange={(date, dateString) => handleCustomFieldChange(column.key, dateString)}
            format="DD.MM.YYYY"
            style={{ width: '100%' }}
          />
        );
      case 'boolean':
        return (
          <Checkbox
            checked={Boolean(value)}
            onChange={(e) => handleCustomFieldChange(column.key, e.target.checked)}
          >
            {column.title}
          </Checkbox>
        );
      default:
        return (
          <StyledInput
            value={value || ''}
            onChange={(e) => handleCustomFieldChange(column.key, e.target.value)}
            placeholder={`Введите ${column.title.toLowerCase()}`}
          />
        );
    }
  };

  // Добавляем функцию для скачивания файла
  const handleDownloadDocument = async () => {
    if (!laboratoryId || !editedRecord.id) return;
    
    try {
      setIsDownloading(true);
      let response;
      let fileName = 'document';
      
      switch (type) {
        case INSTRUMENT_TYPES.MEASURING:
          response = await instrumentsApi.downloadMeasuringDocument(laboratoryId, editedRecord.id);
          fileName = (editedRecord as MeasuringInstrumentType).ownership_document || 'document';
          break;
        case INSTRUMENT_TYPES.TESTING:
          response = await instrumentsApi.downloadTestingDocument(laboratoryId, editedRecord.id);
          fileName = (editedRecord as TestInstrumentType).ownership_document || 'document';
          break;
        case INSTRUMENT_TYPES.HELPING:
          response = await instrumentsApi.downloadHelpingDocument(laboratoryId, editedRecord.id);
          fileName = (editedRecord as HelpInstrumentType).ownership_document || 'document';
          break;
        default:
          throw new Error('Unsupported instrument type');
      }
      
      // Создаем ссылку для скачивания
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
      message.error('Ошибка при скачивании файла');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleSave = async () => {
    if (!laboratoryId) return;

    try {
      setLoading(true);
      
      switch (type) {
        case INSTRUMENT_TYPES.MEASURING: {
          const measuringRecord = editedRecord as MeasuringInstrumentType;
          const formData = new FormData();
          
          // Добавляем файл, если он есть
          if (ownershipDocumentFile) {
            formData.append('ownership_document', ownershipDocumentFile);
          }
          
          const dataToSend = {
            number: measuringRecord.number,
            status: measuringRecord.status,
            characteristics: measuringRecord.characteristics,
            name: measuringRecord.name,
            manufacturer: measuringRecord.manufacturer,
            commissioning_year: measuringRecord.commissioning_year,
            measurement_range: measuringRecord.measurement_range,
            accuracy: measuringRecord.accuracy,
            verification_certificate: measuringRecord.verification_certificate,
            verification_date: measuringRecord.verification_date,
            next_verification_date: measuringRecord.next_verification_date,
            registration_number: measuringRecord.registration_number,
            verification_method: measuringRecord.verification_method,
            ownership_document: measuringRecord.ownership_document,
            location: measuringRecord.location,
            notes: measuringRecord.notes,
            custom_columns: customFieldValues
          };
          
          formData.append('instrument_data', JSON.stringify(dataToSend));
          
          await instrumentsApi.updateMeasuringInstrument(laboratoryId, record.id, formData);
          break;
        }

        case INSTRUMENT_TYPES.TESTING: {
          const testRecord = editedRecord as TestInstrumentType;
          const formData = new FormData();
          
          // Добавляем файл, если он есть
          if (ownershipDocumentFile) {
            formData.append('ownership_document', ownershipDocumentFile);
          }
          
          const dataToSend = {
            number: testRecord.number,
            status: testRecord.status,
            characteristics: testRecord.characteristics,
            test_groups: testRecord.test_groups,
            testing_equipment: testRecord.testing_equipment,
            manufacturer: testRecord.manufacturer,
            technical_characteristics: testRecord.technical_characteristics,
            commissioning_year: testRecord.commissioning_year,
            document_date_and_number: testRecord.document_date_and_number,
            ownership_document: testRecord.ownership_document,
            location: testRecord.location,
            notes: testRecord.notes,
            custom_columns: customFieldValues
          };
          
          formData.append('instrument_data', JSON.stringify(dataToSend));
          
          await instrumentsApi.updateTestingInstrument(laboratoryId, testRecord.id, formData);
          break;
        }

        case INSTRUMENT_TYPES.HELPING: {
          const helpRecord = editedRecord as HelpInstrumentType;
          const formData = new FormData();
          
          // Добавляем файл, если он есть
          if (ownershipDocumentFile) {
            formData.append('ownership_document', ownershipDocumentFile);
          }
          
          const dataToSend = {
            number: helpRecord.number,
            status: helpRecord.status,
            name: helpRecord.name,
            manufacturer: helpRecord.manufacturer,
            commissioning_year: helpRecord.commissioning_year,
            purpose: helpRecord.purpose,
            ownership_document: helpRecord.ownership_document,
            location: helpRecord.location,
            notes: helpRecord.notes,
            custom_columns: customFieldValues
          };
          
          formData.append('instrument_data', JSON.stringify(dataToSend));
          
          await instrumentsApi.updateHelpingInstrument(laboratoryId, record.id, formData);
          break;
        }
      }
      
      message.success('Запись успешно обновлена');
      onSuccess?.();
      onCancel();
    } catch (error) {
      if (error instanceof Error) {
        message.error(error.message);
      } else {
        message.error('Ошибка при обновлении записи');
      }
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    Modal.confirm({
      title: 'Подтверждение удаления',
      content: 'Вы уверены, что хотите удалить эту запись?',
      okText: 'Удалить',
      cancelText: 'Отмена',
      centered: true,
      icon: null,
      okButtonProps: { danger: true },
      onCancel: () => {
        console.log('Cancel clicked');
      },
      width: 500,
      onOk: async () => {
        if (!laboratoryId) return;

        try {
          setLoading(true);
          
          switch (type) {
            case INSTRUMENT_TYPES.MEASURING:
              await instrumentsApi.deleteMeasuringInstrument(laboratoryId, record.id);
              break;
            case INSTRUMENT_TYPES.TESTING:
              await instrumentsApi.deleteTestingInstrument(laboratoryId, record.id);
              break;
            case INSTRUMENT_TYPES.HELPING:
              await instrumentsApi.deleteHelpingInstrument(laboratoryId, record.id);
              break;
          }
          
          message.success('Запись успешно удалена');
          onSuccess?.();
          onCancel();
        } catch (error) {
          if (error instanceof Error) {
            message.error(error.message);
          } else {
            message.error('Ошибка при удалении записи');
          }
          console.error('Error:', error);
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const renderFields = () => {
    switch (type) {
      case INSTRUMENT_TYPES.MEASURING:
        const measuringRecord = editedRecord as MeasuringInstrumentType;
        return (
          <>
            <Col span={24}>
              <StyledText>Статус оборудования</StyledText>
              <StyledSelect
                value={measuringRecord.status}
                onChange={handleSelectChange('status')}
                options={[
                  'В работе',
                  'На поверке',
                  'В ремонте',
                  'Списан'
                ].map(item => ({ label: item, value: item }))}
                style={{ width: '100%' }}
              />
            </Col>
            <Col span={24}>
              <StyledText>Наименование определяемых характеристик</StyledText>
              <StyledInput
                value={measuringRecord.characteristics}
                onChange={(e) => handleSelectChange('characteristics')(e.target.value)}
              />
            </Col>
            <Col span={24}>
              <StyledText>Наименование СИ</StyledText>
              <StyledInput
                value={measuringRecord.name}
                onChange={(e) => handleSelectChange('name')(e.target.value)}
              />
            </Col>
            <Col span={24}>
              <StyledText>Изготовитель</StyledText>
              <StyledInput
                value={measuringRecord.manufacturer}
                onChange={(e) => handleSelectChange('manufacturer')(e.target.value)}
              />
            </Col>
            <Col span={24}>
              <StyledText>Год ввода в эксплуатацию</StyledText>
              <StyledInput
                type="number"
                value={measuringRecord.commissioning_year}
                onChange={(e) => handleSelectChange('commissioning_year')(Number(e.target.value))}
              />
            </Col>
            <Col span={24}>
              <StyledText>Диапазон измерени</StyledText>
              <StyledInput
                value={measuringRecord.measurement_range}
                onChange={(e) => handleSelectChange('measurement_range')(e.target.value)}
              />
            </Col>
            <Col span={24}>
              <StyledText>Класс точности, погрешность</StyledText>
              <StyledInput
                value={measuringRecord.accuracy}
                onChange={(e) => handleSelectChange('accuracy')(e.target.value)}
                placeholder="введите класс точности"
              />
            </Col>
            <Col span={24}>
              <StyledText>Свидетельство о поверке</StyledText>
              <StyledInput
                value={measuringRecord.verification_certificate}
                onChange={(e) => handleSelectChange('verification_certificate')(e.target.value)}
              />
            </Col>
            <Col span={24}>
              <StyledText>Дата поверки / калибровки</StyledText>
              <StyledDatePicker
                value={measuringRecord.verification_date ? dayjs(measuringRecord.verification_date) : null}
                onChange={handleDateChange('verification_date')}
                format="DD.MM.YYYY"
                style={{ width: '100%' }}
              />
            </Col>
            <Col span={24}>
              <StyledText>Дата проведения следующей проверки</StyledText>
              <StyledDatePicker
                value={measuringRecord.next_verification_date ? dayjs(measuringRecord.next_verification_date) : null}
                onChange={handleDateChange('next_verification_date')}
                format="DD.MM.YYYY"
                style={{ width: '100%' }}
              />
            </Col>
            <Col span={24}>
              <StyledText>Регистрационный номер</StyledText>
              <StyledInput
                value={measuringRecord.registration_number}
                onChange={(e) => handleSelectChange('registration_number')(e.target.value)}
              />
            </Col>
            <Col span={24}>
              <StyledText>Методика поверки</StyledText>
              <StyledInput
                value={measuringRecord.verification_method}
                onChange={(e) => handleSelectChange('verification_method')(e.target.value)}
                placeholder="введите методику"
              />
            </Col>
            <Col span={24}>
              <StyledText>Право собственности или документ</StyledText>
              <Row gutter={8} align="middle">
                <Col flex="auto">
                  <StyledInput
                    value={measuringRecord.ownership_document || 'Файл не выбран'}
                    readOnly
                    placeholder="Файл не выбран"
                  />
                </Col>
                <Col>
                  <Upload
                    accept=".pdf,.doc,.docx,.txt"
                    showUploadList={false}
                    beforeUpload={(file) => {
                      const maxSize = 20 * 1024 * 1024; // 20MB
                      if (file.size > maxSize) {
                        message.error('Размер файла не должен превышать 20MB');
                        return Upload.LIST_IGNORE;
                      }
                      setOwnershipDocumentFile(file);
                      setEditedRecord(prev => ({
                        ...prev,
                        ownership_document: file.name
                      }));
                      return false;
                    }}
                  >
                    <Button style={{ height: 40 }} icon={<UploadOutlined />}>Выбрать</Button>
                  </Upload>
                </Col>
                {measuringRecord.ownership_document && (
                  <>
                    <Col>
                      <Button
                        style={{ height: 40 }}
                        icon={<DeleteOutlined />}
                        onClick={() => {
                          setOwnershipDocumentFile(null);
                          setEditedRecord(prev => ({
                            ...prev,
                            ownership_document: ''
                          }));
                        }}
                      />
                    </Col>
                    {/* {!ownershipDocumentFile && (
                      <Col>
                        <Button
                          style={{ height: 40 }}
                          icon={<DownloadOutlined />}
                          onClick={handleDownloadDocument}
                          loading={isDownloading}
                        />
                      </Col>
                    )} */}
                  </>
                )}
              </Row>
              <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
                До 20МБ. Форматы: PDF, DOC, DOCX, TXT
              </div>
            </Col>
            <Col span={24}>
              <StyledText>Место установки или хранения</StyledText>
              <StyledInput
                value={measuringRecord.location}
                onChange={(e) => handleSelectChange('location')(e.target.value)}
                placeholder="введите место"
              />
            </Col>
            <Col span={24}>
              <StyledText>Примечания</StyledText>
              <StyledInput
                value={measuringRecord.notes}
                onChange={(e) => handleSelectChange('notes')(e.target.value)}
              />
            </Col>
            
            {/* Добавляем кастомные поля */}
            {customColumns.map(column => (
              <Col span={24} key={column.key}>
                <StyledText>{column.title}</StyledText>
                {renderCustomField(column)}
              </Col>
            ))}
          </>
        );

      case INSTRUMENT_TYPES.TESTING:
        const testRecord = editedRecord as TestInstrumentType;
        return (
          <>
            <Col span={24}>
              <StyledText>Статус оборудования</StyledText>
              <StyledSelect
                value={testRecord.status}
                onChange={handleSelectChange('status')}
                options={[
                  'В работе',
                  'На аттестации',
                  'В ремонте',
                  'Списан'
                ].map(item => ({ label: item, value: item }))}
                style={{ width: '100%' }}
              />
            </Col>
            <Col span={24}>
              <StyledText>Наименование определяемых характеристик</StyledText>
              <StyledInput
                value={testRecord.characteristics}
                onChange={(e) => handleSelectChange('characteristics')(e.target.value)}
              />
            </Col>
            <Col span={24}>
              <StyledText>Наименование испытуемых групп объектов</StyledText>
              <StyledInput
                value={testRecord.test_groups}
                onChange={(e) => handleSelectChange('test_groups')(e.target.value)}
              />
            </Col>
            <Col span={24}>
              <StyledText>Наименование спытательного оборудования</StyledText>
              <StyledInput
                value={testRecord.testing_equipment}
                onChange={(e) => handleSelectChange('testing_equipment')(e.target.value)}
              />
            </Col>
            <Col span={24}>
              <StyledText>Изготовитель</StyledText>
              <StyledInput
                value={testRecord.manufacturer}
                onChange={(e) => handleSelectChange('manufacturer')(e.target.value)}
              />
            </Col>
            <Col span={24}>
              <StyledText>Основные тех. характеристики</StyledText>
              <StyledInput
                value={testRecord.technical_characteristics}
                onChange={(e) => handleSelectChange('technical_characteristics')(e.target.value)}
              />
            </Col>
            <Col span={24}>
              <StyledText>Год ввода в эксплуатацию</StyledText>
              <StyledInput
                type="number"
                value={testRecord.commissioning_year}
                onChange={(e) => handleSelectChange('commissioning_year')(Number(e.target.value))}
              />
            </Col>
            <Col span={24}>
              <StyledText>Дата и номер документа</StyledText>
              <StyledInput
                value={testRecord.document_date_and_number}
                onChange={(e) => handleSelectChange('document_date_and_number')(e.target.value)}
              />
            </Col>
            <Col span={24}>
              <StyledText>Право собственности или документ</StyledText>
              <Row gutter={8} align="middle">
                <Col flex="auto">
                  <StyledInput
                    value={testRecord.ownership_document || 'Файл не выбран'}
                    readOnly
                    placeholder="Файл не выбран"
                  />
                </Col>
                <Col>
                  <Upload
                    accept=".pdf,.doc,.docx,.txt"
                    showUploadList={false}
                    beforeUpload={(file) => {
                      const maxSize = 20 * 1024 * 1024; // 20MB
                      if (file.size > maxSize) {
                        message.error('Размер файла не должен превышать 20MB');
                        return Upload.LIST_IGNORE;
                      }
                      setOwnershipDocumentFile(file);
                      setEditedRecord(prev => ({
                        ...prev,
                        ownership_document: file.name
                      }));
                      return false;
                    }}
                  >
                    <Button style={{ height: 40 }} icon={<UploadOutlined />}>Выбрать</Button>
                  </Upload>
                </Col>
                {testRecord.ownership_document && (
                  <>
                    <Col>
                      <Button
                        style={{ height: 40 }}
                        icon={<DeleteOutlined />}
                        onClick={() => {
                          setOwnershipDocumentFile(null);
                          setEditedRecord(prev => ({
                            ...prev,
                            ownership_document: ''
                          }));
                        }}
                      />
                    </Col>
                  </>
                )}
              </Row>
              <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
                До 20МБ. Форматы: PDF, DOC, DOCX, TXT
              </div>
            </Col>
            <Col span={24}>
              <StyledText>Место установки или хранения</StyledText>
              <StyledInput
                value={testRecord.location}
                onChange={(e) => handleSelectChange('location')(e.target.value)}
                placeholder="введите место"
              />
            </Col>
            <Col span={24}>
              <StyledText>Примечания</StyledText>
              <StyledInput
                value={testRecord.notes}
                onChange={(e) => handleSelectChange('notes')(e.target.value)}
              />
            </Col>
            
            {/* Добавляем кастомные поля */}
            {customColumns.map(column => (
              <Col span={24} key={column.key}>
                <StyledText>{column.title}</StyledText>
                {renderCustomField(column)}
              </Col>
            ))}
          </>
        );

      case INSTRUMENT_TYPES.HELPING:
        const helpRecord = editedRecord as HelpInstrumentType;
        return (
          <>
            <Col span={24}>
              <StyledText>Статус оборудования</StyledText>
              <StyledSelect
                value={helpRecord.status}
                onChange={handleSelectChange('status')}
                options={[
                  'В работе',
                  'На обслуживании',
                  'В ремонте',
                  'Списан'
                ].map(item => ({ label: item, value: item }))}
                style={{ width: '100%' }}
              />
            </Col>
            <Col span={24}>
              <StyledText>Наименование</StyledText>
              <StyledInput
                value={helpRecord.name}
                onChange={(e) => handleSelectChange('name')(e.target.value)}
              />
            </Col>
            <Col span={24}>
              <StyledText>Изготовитель</StyledText>
              <StyledInput
                value={helpRecord.manufacturer}
                onChange={(e) => handleSelectChange('manufacturer')(e.target.value)}
              />
            </Col>
            <Col span={24}>
              <StyledText>Год ввода в эксплуатацию</StyledText>
              <StyledInput
                type="number"
                value={helpRecord.commissioning_year}
                onChange={(e) => handleSelectChange('commissioning_year')(Number(e.target.value))}
              />
            </Col>
            <Col span={24}>
              <StyledText>Назначение</StyledText>
              <StyledInput
                value={helpRecord.purpose}
                onChange={(e) => handleSelectChange('purpose')(e.target.value)}
              />
            </Col>
            <Col span={24}>
              <StyledText>Право собственности или документ</StyledText>
              <Row gutter={8} align="middle">
                <Col flex="auto">
                  <StyledInput
                    value={helpRecord.ownership_document || 'Файл не выбран'}
                    readOnly
                    placeholder="Файл не выбран"
                  />
                </Col>
                <Col>
                  <Upload
                    accept=".pdf,.doc,.docx,.txt"
                    showUploadList={false}
                    beforeUpload={(file) => {
                      const maxSize = 20 * 1024 * 1024; // 20MB
                      if (file.size > maxSize) {
                        message.error('Размер файла не должен превышать 20MB');
                        return Upload.LIST_IGNORE;
                      }
                      setOwnershipDocumentFile(file);
                      setEditedRecord(prev => ({
                        ...prev,
                        ownership_document: file.name
                      }));
                      return false;
                    }}
                  >
                    <Button style={{ height: 40 }} icon={<UploadOutlined />}>Выбрать</Button>
                  </Upload>
                </Col>
                {helpRecord.ownership_document && (
                  <>
                    <Col>
                      <Button
                        style={{ height: 40 }}
                        icon={<DeleteOutlined />}
                        onClick={() => {
                          setOwnershipDocumentFile(null);
                          setEditedRecord(prev => ({
                            ...prev,
                            ownership_document: ''
                          }));
                        }}
                      />
                    </Col>
                    {/* {!ownershipDocumentFile && (
                      <Col>
                        <Button
                          style={{ height: 40 }}
                          icon={<DownloadOutlined />}
                          onClick={handleDownloadDocument}
                          loading={isDownloading}
                        />
                      </Col>
                    )} */}
                  </>
                )}
              </Row>
              <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
                До 20МБ. Форматы: PDF, DOC, DOCX, TXT
              </div>
            </Col>
            <Col span={24}>
              <StyledText>Место установки или хранения</StyledText>
              <StyledInput
                value={helpRecord.location}
                onChange={(e) => handleSelectChange('location')(e.target.value)}
                placeholder="введите место"
              />
            </Col>
            <Col span={24}>
              <StyledText>Примечания</StyledText>
              <StyledInput
                value={helpRecord.notes}
                onChange={(e) => handleSelectChange('notes')(e.target.value)}
              />
            </Col>
            
            {/* Добавляем кастомные поля */}
            {customColumns.map(column => (
              <Col span={24} key={column.key}>
                <StyledText>{column.title}</StyledText>
                {renderCustomField(column)}
              </Col>
            ))}
          </>
        );
    }
  };

  return (
    <ScreenContainer>
      <HeaderContainer>
        <ScreenTitle level={4}>Изменение записи</ScreenTitle>
      </HeaderContainer>

      <ScrollContent>
        <Row gutter={[16, 16]}>
          {renderFields()}
        </Row>
      </ScrollContent>

      <BottomButtons>
        <ButtonsRow>
          <DeleteButton 
            icon={<DeleteOutlined style={{ color: token['red-5'] }} />}
            onClick={handleDelete}
          />
          <Button 
            className="cancel-button"
            onClick={onCancel}
            disabled={loading}
          >
            Отмена
          </Button>
          <Button 
            className="action-button"
            type="primary"
            onClick={handleSave}
            loading={loading}
          >
            Сохранить
          </Button>
        </ButtonsRow>
      </BottomButtons>
    </ScreenContainer>
  );
};

// Добавляем стили для кнопки удаления
const DeleteButton = styled(Button)`
  min-width: 46px !important;
  width: 46px !important;
  height: 40px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 46px !important;
`;

export default EditInstrument;

